import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Download, Crown, ArrowUpDown } from 'lucide-react';
import {
  listSupplierQuotes, createSupplierQuote, updateSupplierQuote, deleteSupplierQuote,
  listSuppliers, listRfqCampaigns, exportSupplierQuotesUrl,
} from '../../lib/rfqApi';

const GRADES = ['A', 'A-', 'B+', 'B', 'C'];
const EMPTY = {
  supplier_id: '', campaign_id: '', model: '', brand: '', generation: '',
  cpu: '', ram: '', storage: '', screen_size: '', grade: '',
  quantity_available: '', unit_price: '', warranty: '', shipping_origin: '',
  lead_time: '', charger_included: null, battery_health: '', notes: '',
};

export const AdminSupplierQuotes = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [campaignFilter, setCampaignFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [sortBy, setSortBy] = useState('unit_price');
  const [sortDir, setSortDir] = useState('asc');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, sort_dir: sortDir };
      if (campaignFilter) params.campaign_id = campaignFilter;
      if (brandFilter) params.brand = brandFilter;
      if (modelFilter) params.model = modelFilter;
      const { data } = await listSupplierQuotes(params);
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  }, [campaignFilter, brandFilter, modelFilter, sortBy, sortDir]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    (async () => {
      try { const [c, s] = await Promise.all([listRfqCampaigns(), listSuppliers()]);
        setCampaigns(c.data.items || []); setSuppliers(s.data.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      supplier_id: q.supplier_id || '', campaign_id: q.campaign_id || '',
      model: q.model || '', brand: q.brand || '', generation: q.generation || '',
      cpu: q.cpu || '', ram: q.ram || '', storage: q.storage || '',
      screen_size: q.screen_size || '', grade: q.grade || '',
      quantity_available: q.quantity_available ?? '', unit_price: q.unit_price ?? '',
      warranty: q.warranty || '', shipping_origin: q.shipping_origin || '',
      lead_time: q.lead_time || '', charger_included: q.charger_included,
      battery_health: q.battery_health || '', notes: q.notes || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { toast.error('Supplier is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_available: form.quantity_available === '' ? null : Number(form.quantity_available),
        unit_price: form.unit_price === '' ? null : Number(form.unit_price),
      };
      if (!payload.campaign_id) delete payload.campaign_id;
      if (editing) {
        await updateSupplierQuote(editing.id, payload);
        toast.success('Quote updated');
      } else {
        await createSupplierQuote(payload);
        toast.success('Quote created');
      }
      setModalOpen(false); fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm('Delete this quote?')) return;
    try { await deleteSupplierQuote(q.id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const exportHref = () => {
    const url = exportSupplierQuotesUrl(campaignFilter || null);
    // CSV endpoint requires Authorization header; open via fetch then download
    const token = localStorage.getItem('luxortek_admin_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'supplier_quotes.csv';
        a.click();
      })
      .catch(() => toast.error('Export failed'));
  };

  const Th = ({ field, children }) => (
    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className={`w-3 h-3 ${sortBy === field ? 'text-[#00D4FF]' : 'text-white/30'}`} /></span>
    </th>
  );

  return (
    <div className="p-6 lg:p-8 text-white" data-testid="admin-supplier-quotes">
      <Toaster theme="dark" />
      <div className="flex flex-col gap-1 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Supplier Quotes</h1>
        <p className="text-white/50 text-sm">Track and compare supplier responses.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <select data-testid="quotes-campaign-filter" value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-sm text-sm">
          <option value="">All campaigns</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.campaign_name}</option>)}
        </select>
        <input data-testid="quotes-brand-filter" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} placeholder="Brand (HP/Dell/…)" className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-sm text-sm" />
        <input data-testid="quotes-model-filter" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} placeholder="Model contains…" className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-sm text-sm flex-1" />
        <button onClick={exportHref} data-testid="quotes-export-csv" className="flex items-center gap-2 px-4 py-2.5 border border-white/10 text-sm hover:bg-white/5"><Download className="w-4 h-4" />CSV</button>
        <button onClick={openCreate} data-testid="quotes-create-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#00D4FF] text-black font-semibold text-sm"><Plus className="w-4 h-4" />Add Quote</button>
      </div>

      <div className="border border-white/10 overflow-x-auto" data-testid="quotes-table">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
            <tr>
              <Th field="supplier">Supplier</Th>
              <th className="text-left px-4 py-3">Brand / Model</th>
              <th className="text-left px-4 py-3">Specs</th>
              <Th field="grade">Grade</Th>
              <Th field="quantity_available">Qty</Th>
              <Th field="unit_price">Unit Price</Th>
              <Th field="shipping_origin">Origin</Th>
              <th className="text-left px-4 py-3">Lead</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="px-4 py-10 text-center text-white/40">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-white/40">No quotes recorded yet.</td></tr>
            )}
            {!loading && items.map((q) => (
              <tr key={q.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${q.is_best_price ? 'bg-emerald-400/5' : ''}`} data-testid={`quote-row-${q.id}`}>
                <td className="px-4 py-3">
                  <div>{q.supplier_company_name || '—'}</div>
                  <div className="text-xs text-white/40">{q.supplier_country || ''}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{q.brand || '—'} {q.model || ''}</div>
                  {q.generation && <div className="text-xs text-white/40">{q.generation}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-white/60">
                  {[q.cpu, q.ram, q.storage, q.screen_size].filter(Boolean).join(' · ') || '—'}
                  {q.battery_health && <div className="text-white/40">Battery: {q.battery_health}</div>}
                </td>
                <td className="px-4 py-3">{q.grade || '—'}</td>
                <td className="px-4 py-3">{q.quantity_available ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    {q.is_best_price && <Crown className="w-3.5 h-3.5 text-emerald-300" title="Best price for this model" />}
                    <span className={q.is_best_price ? 'text-emerald-200 font-semibold' : ''}>${q.unit_price?.toFixed?.(2) ?? '—'}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-white/60 text-xs">{q.shipping_origin || '—'}</td>
                <td className="px-4 py-3 text-white/60 text-xs">{q.lead_time || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(q)} data-testid={`quote-edit-${q.id}`} className="p-1.5 text-white/60 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(q)} data-testid={`quote-delete-${q.id}`} className="p-1.5 text-white/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setModalOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}
                className="w-full max-w-3xl bg-[#0A0A0A] border border-white/10 max-h-[92vh] overflow-y-auto"
                data-testid="quote-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h2 className="text-lg font-semibold">{editing ? 'Edit Quote' : 'Add Quote'}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="Supplier *">
                <select required data-testid="quote-field-supplier" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="ipt">
                  <option value="">Select supplier…</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                </select>
              </Field>
              <Field label="Campaign (optional)">
                <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className="ipt">
                  <option value="">—</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.campaign_name}</option>)}
                </select>
              </Field>
              <Field label="Brand"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="ipt" /></Field>
              <Field label="Model"><input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="ipt" /></Field>
              <Field label="Generation"><input value={form.generation} onChange={(e) => setForm({ ...form, generation: e.target.value })} className="ipt" /></Field>
              <Field label="CPU"><input value={form.cpu} onChange={(e) => setForm({ ...form, cpu: e.target.value })} className="ipt" /></Field>
              <Field label="RAM"><input value={form.ram} onChange={(e) => setForm({ ...form, ram: e.target.value })} className="ipt" /></Field>
              <Field label="Storage"><input value={form.storage} onChange={(e) => setForm({ ...form, storage: e.target.value })} className="ipt" /></Field>
              <Field label="Screen size"><input value={form.screen_size} onChange={(e) => setForm({ ...form, screen_size: e.target.value })} className="ipt" /></Field>
              <Field label="Grade">
                <select value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="ipt">
                  <option value="">—</option>
                  {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Quantity available"><input type="number" data-testid="quote-field-qty" value={form.quantity_available} onChange={(e) => setForm({ ...form, quantity_available: e.target.value })} className="ipt" /></Field>
              <Field label="Unit price (USD)"><input type="number" step="0.01" data-testid="quote-field-price" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="ipt" /></Field>
              <Field label="Warranty"><input value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} className="ipt" /></Field>
              <Field label="Shipping origin"><input value={form.shipping_origin} onChange={(e) => setForm({ ...form, shipping_origin: e.target.value })} className="ipt" /></Field>
              <Field label="Lead time"><input value={form.lead_time} onChange={(e) => setForm({ ...form, lead_time: e.target.value })} className="ipt" /></Field>
              <Field label="Battery health"><input value={form.battery_health} onChange={(e) => setForm({ ...form, battery_health: e.target.value })} className="ipt" placeholder="e.g. 85% or 12-mo guarantee" /></Field>
              <Field label="Charger included">
                <select value={form.charger_included === null ? '' : String(form.charger_included)} onChange={(e) => setForm({ ...form, charger_included: e.target.value === '' ? null : e.target.value === 'true' })} className="ipt">
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ipt resize-none" />
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5 sticky bottom-0 bg-[#0A0A0A]">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button data-testid="quote-save-btn" type="submit" disabled={saving} className="px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm disabled:opacity-50">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
            </div>
          </form>
        </div>
      )}

      <style>{`.ipt{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);padding:0.55rem 0.75rem;font-size:0.875rem;color:white;outline:none}.ipt:focus{border-color:rgba(0,212,255,0.5)}`}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-white/60 mb-1 block">{label}</span>
    {children}
  </label>
);
